import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PageShell from '../components/PageShell';
import mascotFaq from '../assets/mascot/Biddo_FAQ.png';

const ELECTRIC_PURPLE = '#7B2FF7';

const FAQ_CATEGORIES = [
    {
        key: 'general',
        label: 'General',
        items: [
            {
                q: '¿Qué es PlayBid?',
                a: 'PlayBid es una plataforma de subastas en línea especializada en juegos de cartas coleccionables (TCG). Las tiendas publican sus artículos y los coleccionistas pujan en tiempo real.',
            },
            {
                q: '¿Cuánto cuesta usar PlayBid?',
                a: 'Nada. Registrarte y usar la plataforma es gratis tanto para coleccionistas como para tiendas. No cobramos comisión por publicar, pujar o administrar una tienda. Lo único opcional que se paga son las gemas.',
            },
            {
                q: '¿Puedo vender cualquier cosa en PlayBid?',
                a: 'No. PlayBid no es un e-commerce: solo se permiten cartas y productos TCG, y todo debe ofrecerse bajo el formato de subasta. No hay ventas a precio fijo.',
            },
            {
                q: '¿Necesito ser mayor de edad?',
                a: 'Sí. Debes tener al menos 18 años y capacidad legal para celebrar contratos para registrarte, ya sea como coleccionista o como tienda.',
            },
        ],
    },
    {
        key: 'bidding',
        label: 'Pujas y subastas',
        items: [
            {
                q: '¿Cómo funciona una subasta?',
                a: 'Cada artículo tiene un precio inicial y una cuenta regresiva. Mientras corre el tiempo, los coleccionistas pueden pujar. Cuando termina, gana la puja válida más alta.',
            },
            {
                q: '¿Qué son los Créditos?',
                a: 'Son la unidad con la que pujas. Son ilimitados, no se compran y cada Crédito equivale a un peso mexicano (MXN). Por eso, tu puja es un compromiso real de pago, no una cifra simbólica.',
            },
            {
                q: '¿Puedo cancelar una puja?',
                a: 'No. Todas las pujas son ofertas vinculantes y no pueden retractarse una vez enviadas. Revisa bien el monto antes de confirmar.',
            },
            {
                q: '¿Qué son las salas privadas?',
                a: 'Son subastas en vivo a las que se entra con un código que te comparte la tienda. Ingresa el código en la opción "Unirse a sala" para participar.',
            },
        ],
    },
    {
        key: 'winning',
        label: 'Ganar y pagar',
        items: [
            {
                q: 'Gané una subasta, ¿cómo pago?',
                a: 'El pago se hace directamente a la tienda vendedora, por el monto en MXN de tu puja ganadora y con los medios de pago que ella indique. PlayBid no procesa ni garantiza ese pago; actúa como intermediaria de la subasta. Encuentras tus artículos ganados en "Mis victorias".',
            },
            {
                q: '¿Qué pasa si no pago un artículo que gané?',
                a: 'Es un incumplimiento grave de los Términos y Condiciones. Si ocurre en más de tres (3) ocasiones, tu cuenta se suspende de forma definitiva.',
            },
            {
                q: '¿Quién se encarga del envío?',
                a: 'Cada tienda define sus condiciones de entrega (envío, recolección u otros acuerdos). PlayBid no es parte de la transacción entre la tienda y el ganador, así que coordina los detalles directamente con la tienda.',
            },
            {
                q: '¿PlayBid garantiza la autenticidad de las cartas?',
                a: 'No. Los artículos se venden tal como los describe cada tienda, que es responsable de la exactitud de sus descripciones e imágenes. Revisa la reputación de la tienda y las fotos antes de pujar.',
            },
        ],
    },
    {
        key: 'gems',
        label: 'Gemas y avatares',
        items: [
            {
                q: '¿Qué son las gemas?',
                a: 'Son una moneda opcional para desbloquear elementos cosméticos, como avatares para tu perfil. Son independientes de los Créditos: no sirven para pujar ni para pagar artículos.',
            },
            {
                q: '¿Cómo compro gemas?',
                a: 'Desde la tienda de gemas, con pago a través de Mercado Pago. Al comprarlas ayudas a mantener PlayBid gratuito y sin anuncios.',
            },
            {
                q: 'Pagué mis gemas pero no aparecen',
                a: 'La acreditación puede tardar unos momentos mientras Mercado Pago confirma el pago. Si pasado un rato siguen sin aparecer, contáctanos por los canales de soporte.',
            },
            {
                q: '¿Puedo pedir reembolso o transferir mis gemas?',
                a: 'Las compras de gemas son finales; los reembolsos se otorgan solo a discreción de PlayBid o cuando la ley lo exija. Las gemas tampoco son transferibles entre cuentas.',
            },
        ],
    },
    {
        key: 'stores',
        label: 'Tiendas',
        items: [
            {
                q: '¿Cómo registro mi tienda?',
                a: 'Entra a la sección de tiendas desde el inicio y crea tu cuenta. Sin costo ni comisiones.',
            },
            {
                q: '¿Qué puedo publicar?',
                a: 'Únicamente cartas y productos de juegos de cartas coleccionables. Las imágenes y descripciones deben corresponder fielmente al artículo y cumplir con la ley aplicable.',
            },
            {
                q: '¿Pueden retirar una publicación o mi cuenta?',
                a: 'Sí. Podemos pausar o eliminar publicaciones que no sean TCG o contengan material ilegal, explícito u ofensivo. El incumplimiento grave o repetido puede terminar en la baja definitiva de la tienda.',
            },
        ],
    },
    {
        key: 'account',
        label: 'Cuenta',
        items: [
            {
                q: 'Olvidé mi contraseña',
                a: 'En la pantalla de inicio de sesión usa la opción de recuperar contraseña y sigue los pasos que te enviaremos.',
            },
            {
                q: '¿Cómo suben mis niveles de coleccionista?',
                a: 'Tu reputación crece con tu actividad en la plataforma, y puedes ver tu nivel y tu progreso al siguiente en tu perfil.',
            },
            {
                q: 'Tengo otra duda o un problema',
                a: 'Escríbenos por los canales de soporte indicados en la plataforma y con gusto te ayudamos.',
            },
        ],
    },
];

function normalize(text) {
    return text.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

function FaqPage() {
    const [category, setCategory] = useState('all');
    const [query, setQuery] = useState('');
    const [expanded, setExpanded] = useState(false);

    const visible = useMemo(() => {
        const needle = normalize(query.trim());
        return FAQ_CATEGORIES
            .filter((cat) => category === 'all' || cat.key === category)
            .map((cat) => ({
                ...cat,
                items: cat.items.filter(
                    (item) => !needle || normalize(`${item.q} ${item.a}`).includes(needle)
                ),
            }))
            .filter((cat) => cat.items.length > 0);
    }, [category, query]);

    return (
        <PageShell
            title="Preguntas Frecuentes"
            nav={<Button component={RouterLink} to="/" color="inherit" startIcon={<HomeIcon />}>Volver al Inicio</Button>}
            maxWidth="md"
        >
            <Stack alignItems="center" textAlign="center" spacing={1.5} sx={{ mb: 4 }}>
                <Box
                    component="img"
                    src={mascotFaq}
                    alt="Mascota PlayBid pensando"
                    sx={{ width: { xs: 140, sm: 180 }, height: 'auto' }}
                />
                <Typography
                    component="h1"
                    sx={{
                        fontSize: { xs: '2rem', sm: '2.75rem' },
                        fontWeight: 800,
                        letterSpacing: '-0.03em',
                        lineHeight: 1.1,
                    }}
                >
                    ¿En qué te podemos ayudar?
                </Typography>
                <Typography color="text.secondary">
                    Encuentra respuestas sobre subastas, pagos, gemas y tiendas.
                </Typography>
            </Stack>

            <TextField
                fullWidth
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar una pregunta…"
                slotProps={{
                    htmlInput: { 'aria-label': 'Buscar en preguntas frecuentes' },
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    },
                }}
                sx={{ mb: 2.5 }}
            />

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 4 }}>
                {[{ key: 'all', label: 'Todas' }, ...FAQ_CATEGORIES].map((cat) => (
                    <Chip
                        key={cat.key}
                        label={cat.label}
                        clickable
                        color={category === cat.key ? 'primary' : 'default'}
                        variant={category === cat.key ? 'filled' : 'outlined'}
                        onClick={() => setCategory(cat.key)}
                    />
                ))}
            </Stack>

            {visible.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" sx={{ py: 6 }}>
                    No encontramos resultados para "{query}". Prueba con otras palabras.
                </Typography>
            ) : (
                <Stack spacing={4}>
                    {visible.map((cat) => (
                        <Box key={cat.key} component="section">
                            <Typography variant="h6" sx={{ mb: 1.5 }}>
                                {cat.label}
                            </Typography>
                            <Stack spacing={1}>
                                {cat.items.map((item) => {
                                    const id = `${cat.key}-${item.q}`;
                                    return (
                                        <Accordion
                                            key={id}
                                            disableGutters
                                            expanded={expanded === id}
                                            onChange={(_, isOpen) => setExpanded(isOpen ? id : false)}
                                            sx={{ borderRadius: '14px !important', '&:before': { display: 'none' } }}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                sx={{ '& .MuiAccordionSummary-content': { fontWeight: 600 } }}
                                            >
                                                {item.q}
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Typography color="text.secondary">{item.a}</Typography>
                                            </AccordionDetails>
                                        </Accordion>
                                    );
                                })}
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            )}

            <Box
                sx={{
                    mt: 6,
                    p: 3,
                    textAlign: 'center',
                    borderRadius: 4,
                    border: `1px solid ${ELECTRIC_PURPLE}55`,
                    background: `${ELECTRIC_PURPLE}14`,
                }}
            >
                <Typography fontWeight={700} sx={{ mb: 0.5 }}>
                    ¿No encontraste lo que buscabas?
                </Typography>
                <Typography color="text.secondary" variant="body2">
                    Contáctanos por los canales de soporte indicados en la plataforma.
                </Typography>
            </Box>
        </PageShell>
    );
}

export default FaqPage;
